import { useEffect, useState } from 'react'

/** Wie oft nachgesehen wird, ob die Uhr des Geräts gewandert ist. */
export const NACHSEHEN_MS = 20_000

/**
 * Merkt, wenn das Gerät seine Zeitzone wechselt — **und zeichnet dann neu**.
 *
 * **Die Formatierer allein genügen nicht.** `lib/format.ts` baut sie seit
 * 0.21.1 neu, sobald sich der Versatz geändert hat; gefragt werden sie aber
 * nur beim Zeichnen. Kommt kein Neuzeichnen, steht die alte Uhrzeit weiter da.
 *
 * **Und darauf war Verlass genau so lange, wie es zufällig gut ging.** Der
 * Prüflauf holte sich sein Neuzeichnen aus dem Nachladen der Nachrichten
 * (`refetchInterval`) — einmal grün, beim nächsten vollen Lauf rot, ohne dass
 * sich am Code etwas geändert hätte. Ein Zufall, der zweimal anders ausgeht,
 * war nie eine Behebung. Am Gerät ist es dieselbe Lücke: wer abends die Zone
 * umstellt und niemanden schreiben hat, liest weiter die Zeiten von vorhin.
 *
 * Drei Anlässe, und jeder hat einen Grund:
 *
 *  - **Zurück in die App** (`visibilitychange`). Das ist der Weg, den ein
 *    Mensch wirklich geht: in den Einstellungen die Zone stellen, dann
 *    zurückwechseln.
 *  - **Das Fenster bekommt den Fokus.** Am Rechner steht die App neben
 *    anderen Fenstern; dort wechselt die Sichtbarkeit nicht.
 *  - **Alle zwanzig Sekunden.** Für den Fall, dass niemand etwas tut: die
 *    Sommerzeit stellt um drei Uhr nachts um, und ein Telefon mit
 *    automatischer Zeitzone wechselt sie mitten auf der Fahrt.
 *
 * **Verglichen wird der Versatz und nicht der Zonenname** — dieselbe
 * Entscheidung wie in `lib/format.ts`, und aus demselben Grund: für eine
 * Uhrzeit reicht er, und er fängt die Sommerzeit mit. Bleibt er gleich, bleibt
 * auch der Zustand gleich, und React zeichnet gar nicht erst neu.
 */
export function useUhrwechsel(): number {
  const [versatz, setVersatz] = useState(() => new Date().getTimezoneOffset())

  useEffect(() => {
    const sieh = () => {
      const jetzt = new Date().getTimezoneOffset()
      setVersatz((alt) => (alt === jetzt ? alt : jetzt))
    }
    const uhr = window.setInterval(sieh, NACHSEHEN_MS)
    document.addEventListener('visibilitychange', sieh)
    window.addEventListener('focus', sieh)
    return () => {
      window.clearInterval(uhr)
      document.removeEventListener('visibilitychange', sieh)
      window.removeEventListener('focus', sieh)
    }
  }, [])

  return versatz
}
